import { useRef, useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, API_BASE, detailText } from "@/lib/api";
import { formatPhoneLocal } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { THEMES, applyTheme } from "@/lib/themes";
import { toast } from "sonner";
import { Copy, Check, Camera, Trash2, UserPlus, Lock, User, Smartphone, CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import NumberInput from "@/components/app/NumberInput";
import PasswordInput from "@/components/app/PasswordInput";
import { useT } from "@/context/LanguageContext";
import LanguageToggle from "@/components/app/LanguageToggle";

const WEDDING_TYPES = ["Akad Nikah", "Resepsi", "Pemberkatan", "Sangjit", "Lamaran", "Engagement", "Tea Pai", "Siraman", "After Party"];
const STYLES = ["Elegant", "Romantic", "Minimalist", "Modern", "Luxury", "Rustic", "Traditional", "Outdoor", "Classic"];
const COLORS = ["Ivory", "Champagne", "Blush", "Sage", "Burgundy", "Navy", "Terracotta", "Dusty Blue", "Lavender", "Not decided"];
const COMPLETED = ["Venue", "Catering", "Wedding Organizer", "Decoration", "Photography", "Videography", "Makeup Artist", "Wedding Dress", "Suit", "Invitation", "Entertainment", "MC", "Souvenir"];
const CHALLENGES = ["Managing budget", "Knowing what to do first", "Finding trusted vendors", "Managing guests", "Staying on schedule", "Coordinating with family", "Choosing wedding concept", "Managing contracts and payments", "Everything feels overwhelming"];
const PRIORITIES = ["Planning", "Budgeting", "Finding vendors", "Guest management", "Timeline", "Invitations", "AI wedding advice", "Staying organized"];

const OptionCard = ({ selected, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`text-left rounded-2xl border p-4 transition-shadow duration-200 hover:shadow-md ${selected ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-card"}`}>{children}</button>
);

export default function Settings() {
  const { t } = useT();
  const { wedding, user, refresh, logout } = useAuth();
  
  const [form, setForm] = useState({
    partner1_name: wedding?.partner1_name || "",
    partner1_nickname: wedding?.partner1_nickname || "",
    partner2_name: wedding?.partner2_name || "",
    partner2_nickname: wedding?.partner2_nickname || "",
    wedding_date: wedding?.wedding_date || "",
    akad_time: wedding?.akad_time || "",
    venue_ceremony: wedding?.venue_ceremony || "",
    city: wedding?.city || "",
    budget_amount: wedding?.budget_amount || 0,
    guest_count: wedding?.guest_count || 0,
  });

  const [prefs, setPrefs] = useState({
    wedding_types: wedding?.wedding_types || [],
    wedding_styles: wedding?.wedding_styles || [],
    wedding_colors: wedding?.wedding_colors || [],
    completed_items: wedding?.completed_items || [],
    challenges: wedding?.challenges || [],
    priorities: wedding?.priorities || [],
  });

  // Keep state in sync if wedding object updates
  const prevWeddingRef = useRef(wedding);
  if (wedding && wedding !== prevWeddingRef.current) {
    prevWeddingRef.current = wedding;
    setForm({
      partner1_name: wedding.partner1_name || "",
      partner1_nickname: wedding.partner1_nickname || "",
      partner2_name: wedding.partner2_name || "",
      partner2_nickname: wedding.partner2_nickname || "",
      wedding_date: wedding.wedding_date || "",
      akad_time: wedding.akad_time || "",
      venue_ceremony: wedding.venue_ceremony || "",
      city: wedding.city || "",
      budget_amount: wedding.budget_amount || 0,
      guest_count: wedding.guest_count || 0,
    });
    setPrefs({
      wedding_types: wedding.wedding_types || [],
      wedding_styles: wedding.wedding_styles || [],
      wedding_colors: wedding.wedding_colors || [],
      completed_items: wedding.completed_items || [],
      challenges: wedding.challenges || [],
      priorities: wedding.priorities || [],
    });
  }

  // State untuk Ganti Password
  const [passForm, setPassForm] = useState({ current: "", new: "", confirm: "" });
  const [passLoading, setPassLoading] = useState(false);

  // State foto profil (avatar)
  const [avatarVer, setAvatarVer] = useState(0);
  const avatarInput = useRef(null);
  const avatarUrl = user?.has_avatar ? `${API_BASE}/users/${user.user_id}/avatar?v=${avatarVer}` : user?.picture || "";

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error(t("settings.photo_too_large"));
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      setAvatarVer(Date.now());
      toast.success(t("settings.photo_updated"));
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || t("settings.photo_error"));
    }
  };

  const removeAvatar = async () => {
    try {
      await api.delete("/me/avatar");
      await refresh();
      toast.success(t("settings.photo_removed"));
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || t("settings.photo_error"));
    }
  };

  const save = async () => {
    try {
      await api.patch("/wedding", form);
      await refresh();
      toast.success(t("settings.saved"));
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menyimpan perubahan.");
    }
  };
  const setTheme = async (id) => {
    try {
      applyTheme(id);
      await api.patch("/wedding/theme", { theme_id: id });
      await refresh();
      toast.success(t("settings.theme_updated"));
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal mengubah tema.");
    }
  };
  // --- Buat & Kelola Akun Pasangan ---
  const [pfName, setPfName] = useState("");
  const [pfPhone, setPfPhone] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);

  const [partnerList, setPartnerList] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [deletingPartnerId, setDeletingPartnerId] = useState(null);
  const [deletingPartner, setDeletingPartner] = useState(false);

  const loadPartners = useCallback(async () => {
    try {
      setLoadingPartners(true);
      const res = await api.get("/partners");
      setPartnerList(res.data.partners || []);
    } catch (e) {
      console.error("Gagal memuat pasangan", e);
    } finally {
      setLoadingPartners(false);
    }
  }, []);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const invitePartner = async () => {
    if (!pfPhone.trim()) return toast.error(t("settings.partner_phone_required"));
    setInviting(true);
    try {
      const res = await api.post("/partners/invite", { phone: pfPhone.trim(), name: pfName.trim() });
      setInviteResult({ password: res.data.temp_password, wa_sent: res.data.wa_sent, phone: pfPhone.trim() });
      setInviteCopied(false);
      setPfName(""); setPfPhone("");
      loadPartners();
      refresh();
      if (res.data.wa_sent) {
        toast.success("Akun pasangan berhasil dibuat & kredensial dikirim via WhatsApp! 📲");
      } else {
        toast.success("Akun pasangan berhasil dibuat. (WA service offline, salin password di bawah)");
      }
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || t("settings.invite_error"));
    } finally {
      setInviting(false);
    }
  };

  const removePartner = async (partnerId) => {
    try {
      setDeletingPartner(true);
      await api.delete(`/partners/${partnerId}`);
      toast.success("Akun pasangan berhasil dihapus dari workspace");
      setDeletingPartnerId(null);
      loadPartners();
      refresh();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menghapus akun pasangan");
    } finally {
      setDeletingPartner(false);
    }
  };

  const copyTempPassword = async () => {
    if (!inviteResult) return;
    try {
      await navigator.clipboard.writeText(inviteResult.password);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    } catch {}
  };

  const togglePref = (field, val, max) => {
    setPrefs((p) => {
      const cur = new Set(p[field] || []);
      if (cur.has(val)) cur.delete(val); else { if (max && cur.size >= max) return p; cur.add(val); }
      return { ...p, [field]: [...cur] };
    });
  };

  const savePrefs = async () => {
    try {
      await api.patch("/wedding", prefs);
      await refresh();
      toast.success(t("settings.saved"));
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || "Gagal menyimpan preferensi.");
    }
  };

  // Fungsi Ganti Password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPassLoading(true);
    
    if (passForm.new !== passForm.confirm) {
      toast.error("Konfirmasi password baru tidak cocok.");
      setPassLoading(false);
      return;
    }

    try {
      // Gunakan instance axios bersama (baseURL dari REACT_APP_BACKEND_URL + withCredentials)
      await api.post("/auth/change-password", {
        current_password: passForm.current,
        new_password: passForm.new,
      });

      toast.success("Password berhasil diubah! Silakan login kembali.");
      setPassForm({ current: "", new: "", confirm: "" });

      // Karena backend sudah menghapus semua sesi, arahkan ke login
      setTimeout(() => {
        window.location.href = "/login";
      }, 1500);
    } catch (error) {
      toast.error(detailText(error?.response?.data?.detail) || "Terjadi kesalahan koneksi ke server.");
    } finally {
      setPassLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-10">
      <div className="mb-8"><div className="text-xs uppercase tracking-widest text-muted-foreground">{t("settings.section")}</div><h1 className="font-serif text-4xl">{t("settings.title")}</h1></div>
      <Tabs defaultValue="profile">
        <TabsList className="flex-wrap max-md:flex max-md:w-full max-md:flex-nowrap max-md:overflow-x-auto max-md:px-2 max-md:[scrollbar-width:none] max-md:[&::-webkit-scrollbar]:hidden">
          <TabsTrigger className="shrink-0" value="profile" data-testid="settings-tab-profile">{t("settings.tab_profile")}</TabsTrigger>
          <TabsTrigger className="shrink-0" value="preferences" data-testid="settings-tab-preferences">Preferensi Acara</TabsTrigger>
          <TabsTrigger className="shrink-0" value="theme" data-testid="settings-tab-theme">{t("settings.tab_theme")}</TabsTrigger>
          <TabsTrigger className="shrink-0" value="language" data-testid="settings-tab-language">{t("settings.tab_language")}</TabsTrigger>
          <TabsTrigger className="shrink-0" value="partners" data-testid="settings-tab-partners">{t("settings.tab_partners")}</TabsTrigger>
          {/* Tab Baru: Keamanan */}
          <TabsTrigger className="shrink-0" value="security" data-testid="settings-tab-security">Keamanan</TabsTrigger>
        </TabsList>
        <TabsContent value="profile">
          <Card className="p-6 rounded-2xl space-y-4 mt-4">
            {/* Foto Profil */}
            <div className="flex items-center gap-4 pb-2">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" data-testid="settings-avatar-img" className="h-20 w-20 rounded-full object-cover border border-border" />
              ) : (
                <div data-testid="settings-avatar-img" className="h-20 w-20 rounded-full bg-muted flex items-center justify-center text-muted-foreground"><Camera className="h-7 w-7" /></div>
              )}
              <div className="space-y-2">
                <input ref={avatarInput} type="file" accept="image/*" hidden onChange={uploadAvatar} data-testid="settings-avatar-input" />
                <Button type="button" variant="outline" className="rounded-full" onClick={() => avatarInput.current?.click()} data-testid="settings-avatar-upload-btn">
                  <Camera className="h-4 w-4 mr-2" /> {t("settings.change_photo")}
                </Button>
                {user?.has_avatar && (
                  <div>
                    <Button type="button" variant="ghost" size="sm" className="text-destructive h-8 px-3" onClick={removeAvatar} data-testid="settings-avatar-remove-btn">
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t("settings.remove_photo")}
                    </Button>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">{t("settings.photo_hint")}</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>{t("settings.field_p1_name")}</Label><Input value={form.partner1_name} onChange={(e) => setForm({ ...form, partner1_name: e.target.value })} /></div>
              <div><Label>{t("settings.field_p1_nick")}</Label><Input value={form.partner1_nickname} onChange={(e) => setForm({ ...form, partner1_nickname: e.target.value })} /></div>
              <div><Label>{t("settings.field_p2_name")}</Label><Input value={form.partner2_name} onChange={(e) => setForm({ ...form, partner2_name: e.target.value })} /></div>
              <div><Label>{t("settings.field_p2_nick")}</Label><Input value={form.partner2_nickname} onChange={(e) => setForm({ ...form, partner2_nickname: e.target.value })} /></div>
              <div><Label>{t("settings.field_date")}</Label><Input type="date" value={form.wedding_date || ""} onChange={(e) => setForm({ ...form, wedding_date: e.target.value })} /></div>
              <div><Label>{t("settings.field_akad_time")}</Label><Input placeholder="08:00 WIB" value={form.akad_time || ""} onChange={(e) => setForm({ ...form, akad_time: e.target.value })} /></div>
              <div><Label>{t("settings.field_venue_ceremony")}</Label><Input placeholder="Gedung / Masjid..." value={form.venue_ceremony || ""} onChange={(e) => setForm({ ...form, venue_ceremony: e.target.value })} /></div>
              <div><Label>{t("settings.field_city")}</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><Label>{t("settings.field_budget")}</Label><NumberInput prefix="Rp" value={form.budget_amount} onChange={(v) => setForm({ ...form, budget_amount: v })} /></div>
              <div><Label>{t("settings.field_guests")}</Label><NumberInput value={form.guest_count} onChange={(v) => setForm({ ...form, guest_count: v })} /></div>
            </div>
            <Button className="rounded-full" onClick={save} data-testid="save-settings-btn">{t("settings.save_changes")}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="preferences">
          <Card className="p-6 rounded-2xl mt-4 space-y-8">
            <div>
              <h3 className="font-serif text-xl mb-1">Jenis Acara</h3>
              <p className="text-sm text-muted-foreground mb-4">Pilih semua acara yang berlaku</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {WEDDING_TYPES.map((tp) => (
                  <OptionCard key={tp} selected={prefs.wedding_types?.includes(tp)} onClick={() => togglePref("wedding_types", tp)} testid={`pref-type-${tp}`}>
                    <div className="flex items-center gap-2 font-medium">{prefs.wedding_types?.includes(tp) && <Check className="h-4 w-4 text-primary" />}{tp}</div>
                  </OptionCard>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-serif text-xl mb-1">Gaya Pernikahan</h3>
              <p className="text-sm text-muted-foreground mb-4">Maksimal 3 pilihan</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {STYLES.map((s) => (
                  <OptionCard key={s} selected={prefs.wedding_styles?.includes(s)} onClick={() => togglePref("wedding_styles", s, 3)} testid={`pref-style-${s}`}>
                    <div className="font-medium">{s}</div>
                  </OptionCard>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-serif text-xl mb-1">Warna</h3>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => togglePref("wedding_colors", c)} className={`px-3.5 py-1.5 rounded-full text-sm border ${prefs.wedding_colors?.includes(c) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`} data-testid={`pref-color-${c}`}>{c}</button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-serif text-xl mb-1">Vendor yang Sudah Beres</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {COMPLETED.map((c) => (
                  <OptionCard key={c} selected={prefs.completed_items?.includes(c)} onClick={() => togglePref("completed_items", c)} testid={`pref-completed-${c}`}>
                    <div className="flex items-center gap-2 font-medium">{prefs.completed_items?.includes(c) && <Check className="h-4 w-4 text-primary" />}{c}</div>
                  </OptionCard>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-serif text-xl mb-1">Tantangan</h3>
              <p className="text-sm text-muted-foreground mb-4">Maksimal 3 pilihan</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {CHALLENGES.map((c) => (
                  <OptionCard key={c} selected={prefs.challenges?.includes(c)} onClick={() => togglePref("challenges", c, 3)} testid={`pref-challenge-${c}`}>
                    <div className="font-medium">{c}</div>
                  </OptionCard>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-serif text-xl mb-1">Prioritas</h3>
              <div className="flex flex-wrap gap-2">
                {PRIORITIES.map((c) => (
                  <button key={c} type="button" onClick={() => togglePref("priorities", c)} className={`px-3.5 py-1.5 rounded-full text-sm border ${prefs.priorities?.includes(c) ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`} data-testid={`pref-priority-${c}`}>{c}</button>
                ))}
              </div>
            </div>

            <Button className="rounded-full" onClick={savePrefs} data-testid="save-preferences-btn">{t("settings.save_changes")}</Button>
          </Card>
        </TabsContent>

        <TabsContent value="theme">
          <Card className="p-6 rounded-2xl mt-4">
            <p className="text-muted-foreground mb-4">{t("settings.theme_note")}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {THEMES.map((th) => (
                <button key={th.id} data-testid={`theme-palette-option-${th.id}`} onClick={() => setTheme(th.id)} className={`text-left rounded-2xl border p-3 hover:shadow-md transition-shadow duration-200 ${wedding?.theme_id === th.id ? "border-primary ring-2 ring-primary" : "border-border"}`}>
                  <div className="flex gap-1 mb-2">{th.swatches.map((s) => <span key={s} className="h-5 w-5 rounded-full border border-border" style={{ background: s }} />)}</div>
                  <div className="font-serif">{th.name}</div>
                  <div className="text-xs text-muted-foreground">{th.desc}</div>
                </button>
              ))}
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="language">
          <Card className="p-6 rounded-2xl mt-4 space-y-3">
            <div className="text-sm text-muted-foreground">{t("settings.language_note")}</div>
            <LanguageToggle />
          </Card>
        </TabsContent>
        <TabsContent value="partners">
          {/* Card 1: Form Buat Akun Pasangan (Terkunci jika pasangan sudah ada) */}
          <Card className={`p-6 rounded-2xl mt-4 space-y-4 transition-all ${partnerList.length > 0 ? "bg-muted/40 border-muted" : ""}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-medium">
                {partnerList.length > 0 ? (
                  <Lock className="h-4 w-4 text-amber-600" />
                ) : (
                  <UserPlus className="h-4 w-4 text-primary" />
                )}
                <span>{t("settings.add_partner_title")}</span>
              </div>
              {partnerList.length > 0 && (
                <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50 gap-1 font-semibold text-xs">
                  <Lock className="h-3 w-3" /> Form Terkunci
                </Badge>
              )}
            </div>

            {partnerList.length > 0 ? (
              <div className="flex items-start gap-3 p-3.5 rounded-xl bg-amber-50/80 border border-amber-200 text-amber-900 text-xs leading-relaxed">
                <ShieldAlert className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                <div>
                  <span className="font-semibold block mb-0.5">Workspace Terhubung dengan 1 Pasangan</span>
                  Workspace Anda saat ini sudah memiliki 1 akun pasangan terhubung. Hapus akun pasangan pada kartu di bawah terlebih dahulu jika Anda ingin mengganti atau membuat akun pasangan baru.
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">{t("settings.add_partner_desc")}</div>
            )}

            <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <div className="space-y-1.5">
                <Label htmlFor="partner-name">{t("settings.partner_name_opt")}</Label>
                <Input
                  id="partner-name"
                  value={pfName}
                  onChange={(e) => setPfName(e.target.value)}
                  disabled={partnerList.length > 0 || inviting}
                  data-testid="partner-invite-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="partner-phone">{t("settings.partner_phone")}</Label>
                <Input
                  id="partner-phone"
                  value={pfPhone}
                  onChange={(e) => setPfPhone(e.target.value)}
                  placeholder="0812xxxxxxx"
                  inputMode="tel"
                  disabled={partnerList.length > 0 || inviting}
                  data-testid="partner-invite-phone"
                />
              </div>
              <Button
                onClick={invitePartner}
                disabled={partnerList.length > 0 || inviting}
                className="rounded-full gap-1.5"
                data-testid="partner-invite-submit"
              >
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                <span>{inviting ? "Memproses…" : t("settings.create_account")}</span>
              </Button>
            </div>
          </Card>

          {/* Card 2: Informasi Akun Pasangan Terhubung */}
          <Card className="p-6 rounded-2xl mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-medium">
                <User className="h-4 w-4 text-primary" />
                <span>Informasi Akun Pasangan Terhubung</span>
              </div>
              {partnerList.length > 0 && (
                <Badge variant="outline" className="border-green-400 text-green-700 bg-green-50 font-semibold text-xs">
                  1 Pasangan Aktif
                </Badge>
              )}
            </div>

            {loadingPartners ? (
              <div className="py-4 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-primary" /> Memuat data pasangan…
              </div>
            ) : partnerList.length > 0 ? (
              <div className="space-y-3">
                {partnerList.map((p) => (
                  <div key={p.user_id} className="p-4 rounded-xl bg-card border border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm text-foreground">{p.name}</span>
                        {p.must_change_password ? (
                          <Badge variant="outline" className="text-[10px] text-amber-700 bg-amber-50 border-amber-300">
                            Password Sementara
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-green-700 bg-green-50 border-green-300 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Aktif
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-3">
                        <span className="flex items-center gap-1"><Smartphone className="h-3.5 w-3.5 text-muted-foreground" /> {formatPhoneLocal(p.phone)}</span>
                        {p.joined_at && <span>Bergabung: {new Date(p.joined_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</span>}
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="border-destructive/30 text-destructive hover:bg-destructive/10 text-xs rounded-full gap-1.5 self-start sm:self-center"
                      onClick={() => setDeletingPartnerId(p.user_id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Hapus Akun Pasangan</span>
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center text-xs text-muted-foreground border border-dashed border-border rounded-xl">
                Belum ada akun pasangan terhubung di workspace ini. Gunakan form di atas untuk membuat akun pasangan Anda.
              </div>
            )}
          </Card>

          {/* Card 3: Status Akun Anda */}
          <Card className="p-6 rounded-2xl mt-4 space-y-3">
            <div className="text-sm">{t("settings.signed_in_as")} <span className="font-medium">{formatPhoneLocal(user?.phone)}</span></div>
            <div className="text-sm text-muted-foreground">{t("settings.invite_hint")}</div>
            <div><Button variant="ghost" onClick={logout} className="text-destructive">{t("settings.signout")}</Button></div>
          </Card>

          {/* Dialog Konfirmasi Hapus Pasangan */}
          <Dialog open={!!deletingPartnerId} onOpenChange={(open) => { if (!open) setDeletingPartnerId(null); }}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="text-destructive flex items-center gap-2">
                  <Trash2 className="h-5 w-5" /> Hapus Akun Pasangan
                </DialogTitle>
              </DialogHeader>
              <div className="text-xs text-muted-foreground leading-relaxed py-2">
                Apakah Anda yakin ingin menghapus akun pasangan ini dari workspace?
                <br /><br />
                Pasangan Anda akan langsung dikeluarkan dari workspace dan tidak dapat mengakses data pernikahan ini lagi. Nomor HP tersebut nantinya dapat didaftarkan ulang.
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setDeletingPartnerId(null)}>Batal</Button>
                <Button variant="destructive" onClick={() => removePartner(deletingPartnerId)} disabled={deletingPartner}>
                  {deletingPartner ? "Menghapus…" : "Ya, Hapus Pasangan"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Dialog Password Sementara — Ditampilkan sekali dengan status pengiriman WA */}
          <Dialog open={!!inviteResult} onOpenChange={(open) => { if (!open) setInviteResult(null); }}>
            <DialogContent className="max-w-md" data-testid="partner-invite-dialog">
              <DialogHeader>
                <DialogTitle>{t("settings.invite_created_title")}</DialogTitle>
              </DialogHeader>
              {inviteResult && (
                <div className="space-y-4">
                  {inviteResult.wa_sent ? (
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-green-50 border border-green-200 text-xs text-green-900 leading-relaxed">
                      <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold block">Kredensial Dikirim via WhatsApp! 📲</span>
                        Nomor HP <strong>{formatPhoneLocal(inviteResult.phone)}</strong> dan password sementara telah dikirim secara otomatis ke WhatsApp pasangan Anda.
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900 leading-relaxed">
                      <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold block">Layanan WhatsApp Offline</span>
                        Sampaikan password sementara berikut secara pribadi kepada pasangan Anda untuk login pertama kali:
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <code
                      data-testid="partner-temp-password"
                      className="flex-1 rounded-lg bg-muted px-3 py-2.5 font-mono text-base tracking-wide select-all break-all"
                    >
                      {inviteResult.password}
                    </code>
                    <Button
                      type="button"
                      size="icon"
                      variant={inviteCopied ? "default" : "outline"}
                      onClick={copyTempPassword}
                      data-testid="partner-copy-password-btn"
                      aria-label={t("auth.new_password")}
                      title="Copy"
                    >
                      {inviteCopied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <div className="text-xs text-muted-foreground leading-relaxed">{t("settings.temp_password_note")}</div>
                  <div className="flex justify-end">
                    <Button type="button" onClick={copyTempPassword} variant="outline">
                      {inviteCopied ? <Check className="h-4 w-4 mr-2 text-green-600" /> : <Copy className="h-4 w-4 mr-2" />}
                      {inviteCopied ? t("common.copied") : t("common.copy")}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
        {/* TABS KEAMANAN / GANTI PASSWORD */}
        <TabsContent value="security">
          <Card className="p-6 rounded-2xl mt-4 space-y-4">
            <div className="text-sm text-muted-foreground">
              Masukkan password lama Anda, lalu buat password baru yang kuat (minimal 6 karakter).
            </div>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label>Password Saat Ini</Label>
                <PasswordInput 
                  value={passForm.current} 
                  onChange={(e) => setPassForm({ ...passForm, current: e.target.value })}
                  required 
                  data-testid="settings-current-password-input"
                />
              </div>
              <div>
                <Label>Password Baru</Label>
                <PasswordInput 
                  value={passForm.new} 
                  onChange={(e) => setPassForm({ ...passForm, new: e.target.value })}
                  required 
                  data-testid="settings-new-password-input"
                />
              </div>
              <div>
                <Label>Konfirmasi Password Baru</Label>
                <PasswordInput 
                  value={passForm.confirm} 
                  onChange={(e) => setPassForm({ ...passForm, confirm: e.target.value })}
                  required 
                  data-testid="settings-confirm-password-input"
                />
              </div>
              
              <Button type="submit" className="rounded-full" disabled={passLoading}>
                {passLoading ? "Memproses..." : "Ubah Password"}
              </Button>
            </form>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}